import React from "react";
import { FormControl } from "src/hooks/useFormControl";
import { shortID } from "src/helpers/shortID";
import styled, { css } from "styled-components";
import { InputLabel, InputContainerEl } from "src/components/Input";
import { Text } from "src/components/Text";

export interface ITextAreaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  formControl?: FormControl;
  innerRef?: React.Ref<any>;
  error?: boolean;
  label?: string;
  helperText?: string;
  height?: number;
  disabledResize?: boolean;
}

const TextAreaEl = styled.textarea<ITextAreaProps>`
  width: 100%;
  border-radius: ${(p) => p.theme.dimension.borderRadius[0]}px;
  border: 1px solid ${(props) => props.theme.colors.borders.active};
  height: ${(p) => p.height + "px" || "unset"};
  outline: none;
  padding: 6px 12px;
  font-family: ${(props) => props.theme.text.fontFamily.normal};
  font-size: ${(props) => props.theme.text.size.normal};
  resize: ${(p) => (p.disabledResize ? "none" : "unset")};

  ${(props) =>
    props.error &&
    css`
      border-color: ${props.theme.colors.background.danger};
      color: ${props.theme.colors.text.danger};
    `};

  ${(props) =>
    props.readOnly &&
    css`
      border: none;
      height: 32px;
    `};

  ${(props) =>
    props.disabled &&
    css`
      background-color: ${props.theme.colors.background.disabled};
      border-color: ${props.theme.colors.background.disabled};
      color: ${props.theme.colors.text.disabled};
      cursor: not-allowed;
    `};

  ${(props) => css`
    ::placeholder,
    ::-webkit-input-placeholder {
      color: ${props.theme.colors.text.placeholder};
    }
    :-ms-input-placeholder {
      color: ${props.theme.colors.text.placeholder};
    }
  `}
`;

export const TextArea = ({
  id = shortID(),
  label,
  formControl,
  error,
  helperText,
  disabled,
  readOnly,
  name,
  innerRef,
  ...other
}: ITextAreaProps) => {
  const _name = formControl?.name || name;
  const _error = formControl?.errors || error;
  const _helperText = formControl?.errors?.message || helperText;
  const _Ref = formControl?.register || innerRef;

  const elementProps: any = {
    disabled,
    readOnly,
    error: _error,
  };

  return (
    <InputContainerEl>
      <InputLabel {...elementProps} htmlFor={id}>
        {label}
      </InputLabel>
      <TextAreaEl
        {...elementProps}
        {...other}
        id={id}
        ref={_Ref}
        name={_name}
      />
      {_helperText && (
        <Text
          {...elementProps}
          marginTopS
          fontSize="small"
          color={_error ? "danger" : "normal"}
          fontWeight="book"
        >
          {_helperText}
        </Text>
      )}
    </InputContainerEl>
  );
};
